"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";
import { CLERK_READY_TIMEOUT_MS } from "@/lib/api/authToken";
import { reportClerkUnavailable, resetClerkUnavailableReportForTests as resetClerkUnavailableReport } from "@/lib/monitoring/clerkLoadReporter";

/**
 * How long Clerk may take to load before the page says sign-in couldn't load:
 * the same ceiling after which API requests stop waiting for Clerk's token
 * (`lib/api/authToken`), so the page gives up on Clerk once, everywhere.
 */
export const CLERK_LOAD_WAIT_MS = CLERK_READY_TIMEOUT_MS;

/**
 * Whether Clerk has failed to load within `CLERK_LOAD_WAIT_MS` of page start
 * (TX-5483).
 *
 * A browser extension or a DNS or network filter can block Clerk's script, and
 * then `isLoaded` simply never turns true: sign-up's Continue did nothing and
 * the header avatar stayed empty, with nothing reported anywhere. Pass Clerk's
 * `isLoaded`; the answer is false while it is still loading within the wait,
 * true once the wait runs out, and false again if Clerk loads late.
 */
export function useClerkUnavailable(isLoaded) {
  const $ = _c(3);
  const [waitRanOut, setWaitRanOut] = useState(false);
  let t0;
  let t1;
  if ($[0] !== isLoaded) {
    t0 = () => {
      if (isLoaded) {
        return;
      }
      const remaining = Math.max(0, CLERK_LOAD_WAIT_MS - (performance.now() - clockStart()));
      const timer = setTimeout(() => {
        setWaitRanOut(true);
        reportClerkUnavailable(CLERK_LOAD_WAIT_MS);
      }, remaining);
      return () => clearTimeout(timer);
    };
    t1 = [isLoaded];
    $[0] = isLoaded;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  return !isLoaded && waitRanOut;
}

/**
 * When the wait starts: the first time this module loads in the browser, about
 * when the page hydrates — which is when clerk-js starts loading. Navigation
 * start gave Clerk only a second or two on a slow page (TX-5483 review).
 */
let waitStartedAt: number | null = null;
function clockStart(): number {
  return waitStartedAt ??= performance.now();
}
if (typeof window !== "undefined") clockStart();

/** Tests only: a fresh page load — the report not yet sent, the wait starting now. */
export function resetClerkUnavailableReportForTests(): void {
  resetClerkUnavailableReport();
  waitStartedAt = performance.now();
}